/**
 * Router-compat shim — bridges @/lib/router-compat v6 call sites to
 * @tanstack/react-router without hand-rewriting every component.
 * This is the same load-bearing pattern used in Klar's dev-copy migration.
 */
import {
  useNavigate as tsNavigate,
  useLocation as tsLocation,
  useParams as tsParams,
  useSearch as tsSearch,
  useRouter,
  Link as TSLink,
  Navigate as TSNavigate,
  Outlet as TSOutlet,
} from "@tanstack/react-router";
import { useMemo, useCallback, forwardRef, type ComponentProps, type ReactNode } from "react";

// ---------- shared URL parsing ----------

function parseTo(to: string): { pathname: string; search?: Record<string, string>; hash?: string } {
  const hashParts = (to ?? "").split("#");
  const beforeHash = hashParts[0] ?? "";
  const hashStr = hashParts[1];
  const pathParts = beforeHash.split("?");
  const pathname = pathParts[0] || ".";
  const searchStr = pathParts[1];
  // react-router keeps the current path for search-only ("?a=1") and
  // hash-only ("#section") targets; TanStack's "." means current route.
  const result: { pathname: string; search?: Record<string, string>; hash?: string } = { pathname };
  if (searchStr) result.search = Object.fromEntries(new URLSearchParams(searchStr));
  if (hashStr) result.hash = hashStr;
  return result;
}

// ---------- useNavigate ----------

type NavigateOptions = { replace?: boolean; state?: unknown };

type NavigateFn = {
  (to: string | number, options?: NavigateOptions): void;
  (delta: number): void;
};

export function useNavigate(): NavigateFn {
  const tsNav = tsNavigate();
  const router = useRouter();
  return useCallback((to: string | number, options?: NavigateOptions) => {
    if (typeof to === "number") {
      router.history.go(to);
      return;
    }
    const { pathname, search, hash } = parseTo(to);
    tsNav({
      to: pathname,
      search: search as never,
      state: options?.state as never,
      ...(hash !== undefined ? { hash } : {}),
      ...(options?.replace !== undefined ? { replace: options.replace } : {}),
    });
  }, [tsNav, router]) as NavigateFn;
}

// ---------- useLocation ----------

export function useLocation() {
  const loc = tsLocation();
  return useMemo(
    () => ({
      pathname: loc.pathname,
      search: loc.searchStr ? `?${loc.searchStr}` : "",
      hash: loc.hash ?? "",
      state: (loc.state ?? null) as unknown,
      key: loc.pathname + (loc.searchStr ?? ""),
    }),
    [loc.pathname, loc.searchStr, loc.hash, loc.state],
  );
}

// ---------- useParams ----------

export function useParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): T {
  return tsParams({ strict: false } as never) as T;
}


// ---------- useSearchParams (@/lib/router-compat compat) ----------

/**
 * Read a search string into URLSearchParams, unwrapping JSON-quoted values
 * (e.g. `page=%222%22`) that TanStack's stringifier can produce for
 * numeric-looking strings, so callers see plain `2`.
 */
function readParams(searchStr: string): URLSearchParams {
  const raw = new URLSearchParams(searchStr);
  const out = new URLSearchParams();
  raw.forEach((v, k) => {
    const unquoted = v.length > 1 && v.startsWith('"') && v.endsWith('"') ? v.slice(1, -1) : v;
    out.append(k, unquoted);
  });
  return out;
}

export function useSearchParams(): [URLSearchParams, (init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams), opts?: { replace?: boolean }) => void] {
  const loc = tsLocation();
  const nav = tsNavigate();
  const router = useRouter();
  const params = useMemo(() => readParams(loc.searchStr ?? ""), [loc.searchStr]);
  const setParams = useCallback(
    (
      init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams),
      opts?: { replace?: boolean },
    ) => {
      // Functional updaters read the router's live location, not the render
      // snapshot — react-router passes call-time params, and chained updates
      // within one tick must see each other's writes.
      const live = router.state.location;
      const current = readParams(live.searchStr ?? "");
      const next =
        typeof init === "function"
          ? init(current)
          : init instanceof URLSearchParams
            ? init
            : new URLSearchParams(init);
      // TanStack's default stringifier JSON-encodes values, so a numeric string
      // like "2" would round-trip to the URL as %222%22 and read back as `"2"`
      // (unparseable by callers doing Number(...)). Emit numbers as numbers.
      const searchObj: Record<string, string | number> = {};
      next.forEach((v, k) => {
        searchObj[k] = /^-?\d+(\.\d+)?$/.test(v) && String(Number(v)) === v ? Number(v) : v;
      });
      nav({ to: live.pathname, search: searchObj as never, ...(opts?.replace !== undefined ? { replace: opts.replace } : {}) });
    },
    [nav, router],
  );
  return [params, setParams];
}

// ---------- Link ----------

type LinkProps = Omit<ComponentProps<typeof TSLink>, "to"> & {
  to: string;
  replace?: boolean;
  state?: unknown;
  children?: ReactNode;
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { to, replace, state, children, ...rest },
  ref,
) {
  const { pathname, search, hash } = parseTo(to);
  const extra: Record<string, unknown> = {};
  if (hash !== undefined) extra["hash"] = hash;
  if (replace !== undefined) extra["replace"] = replace;
  if (state !== undefined) extra["state"] = state;
  return (
    <TSLink
      ref={ref as never}
      to={pathname as never}
      search={search as never}
      {...extra}
      {...((rest ?? {}) as Record<string, unknown>)}
    >
      {children}
    </TSLink>
  );
});


// ---------- Navigate ----------

export function Navigate({ to, replace, state }: { to: string; replace?: boolean; state?: unknown }) {
  const { pathname, search, hash } = parseTo(to);
  const extra: Record<string, unknown> = {};
  if (hash !== undefined) extra["hash"] = hash;
  if (replace !== undefined) extra["replace"] = replace;
  if (state !== undefined) extra["state"] = state;
  return <TSNavigate to={pathname as never} search={search as never} {...extra} />;
}

// ---------- Outlet ----------

export const Outlet = TSOutlet;

// ---------- NavLink (minimal) ----------

// react-router's NavLink supports function-form className/style receiving
// { isActive, isPending }. Compute active state from the current location so
// existing call sites (e.g. admin sidebar menus) keep working unchanged.
type NavLinkRenderProps = { isActive: boolean; isPending: boolean };

export type NavLinkProps = Omit<LinkProps, "className" | "style"> & {
  className?: string | ((props: NavLinkRenderProps) => string | undefined);
  style?:
    | LinkProps["style"]
    | ((props: NavLinkRenderProps) => LinkProps["style"]);
  end?: boolean;
};

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(function NavLink(
  { to, className, style, end, ...rest },
  ref,
) {
  const loc = tsLocation();
  const target = parseTo(to).pathname;
  const isActive = end
    ? loc.pathname === target
    : loc.pathname === target || loc.pathname.startsWith(target === "/" ? "/" : target + "/");
  const renderProps: NavLinkRenderProps = { isActive, isPending: false };
  const resolvedClassName =
    typeof className === "function" ? className(renderProps) : className;
  const resolvedStyle = typeof style === "function" ? style(renderProps) : style;
  return (
    <Link ref={ref} to={to} className={resolvedClassName} style={resolvedStyle} {...rest} />
  );
});